import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Package, 
  Search, 
  Filter, 
  Plus, 
  Eye, 
  ShoppingCart, 
  AlertTriangle,
  Grid,
  List
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useNotifications } from '../../contexts/NotificationContext';
import { productService } from '../../services/productService';
import { cartService } from '../../services/cartService';
import { Product, Category, Brand } from '../../types';

function ProductList() {
  const { user } = useAuth();
  const { addNotification } = useNotifications();
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [brands, setBrands] = useState<Brand[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('list');
  const [filters, setFilters] = useState({
    search: '',
    categoryId: '',
    brandId: '',
    alertOnly: false
  });

  useEffect(() => {
    loadData();
  }, [filters]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [productsData, categoriesData, brandsData] = await Promise.all([
        productService.getProducts(filters),
        productService.getCategories(),
        productService.getBrands()
      ]);
      
      setProducts(productsData);
      setCategories(categoriesData);
      setBrands(brandsData);
    } catch (error) {
      addNotification({
        userId: user?.id || '',
        title: 'Erreur de chargement',
        message: 'Impossible de charger les produits',
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = (productId: string) => {
    cartService.addToCart(productId, 1);
    addNotification({
      userId: user?.id || '',
      title: 'Produit ajouté',
      message: 'Le produit a été ajouté au panier',
      type: 'success'
    });
  };

  const canManageProducts = user?.role === 'admin' || user?.role === 'responsable_depot' || user?.role === 'responsable_bureau';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="glass-card rounded-xl card-hover">
        <div className="px-6 py-6 sm:p-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="p-2 bg-gradient-to-br from-green-500 to-green-700 rounded-lg shadow-lg">
                <Package className="h-6 w-6 text-white" />
              </div>
              <h1 className="text-3xl font-bold text-gray-900">Produits</h1>
            </div>
            
            <div className="flex items-center space-x-3">
              {/* View Mode Toggle */}
              <div className="flex bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded-md transition-colors ${
                    viewMode === 'list' 
                      ? 'bg-white text-gray-900 shadow-sm' 
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <List className="h-4 w-4" />
                </button>
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 rounded-md transition-colors ${
                    viewMode === 'grid' 
                      ? 'bg-white text-gray-900 shadow-sm' 
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <Grid className="h-4 w-4" />
                </button>
              </div>

              {canManageProducts && (
                <Link
                  to="/products/new"
                  className="btn-primary px-4 py-2 text-white text-sm rounded-lg font-medium transition-all duration-200 flex items-center space-x-2"
                >
                  <Plus className="h-4 w-4" />
                  <span>Nouveau produit</span>
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="glass-card rounded-xl">
        <div className="px-6 py-5 sm:px-8">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-5">
            {/* Search */}
            <div className="sm:col-span-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Rechercher (réf, désignation, code-barres)..."
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white/80"
                />
              </div>
            </div>

            {/* Category Filter */}
            <select
              value={filters.categoryId}
              onChange={(e) => setFilters({ ...filters, categoryId: e.target.value })}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white/80"
            >
              <option value="">Toutes les catégories</option>
              {categories.map(category => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>

            {/* Brand Filter */}
            <select
              value={filters.brandId}
              onChange={(e) => setFilters({ ...filters, brandId: e.target.value })}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white/80"
            >
              <option value="">Toutes les marques</option>
              {brands.map(brand => (
                <option key={brand.id} value={brand.id}>
                  {brand.name}
                </option>
              ))}
            </select>

            {/* Alert Filter */}
            <label className="flex items-center space-x-2 text-sm">
              <input
                type="checkbox"
                checked={filters.alertOnly}
                onChange={(e) => setFilters({ ...filters, alertOnly: e.target.checked })}
                className="rounded border-gray-300 text-orange-600 focus:ring-orange-500"
              />
              <span className="text-gray-700">En alerte</span>
            </label>
          </div>
        </div>
      </div>

      {/* Products */}
      <div className="glass-card rounded-xl">
        <div className="px-6 py-5 sm:px-8 border-b border-gray-200/50">
          <h3 className="text-lg font-medium text-gray-900">
            Produits ({products.length})
          </h3>
        </div>

        {products.length === 0 ? (
          <div className="text-center py-12">
            <Package className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">Aucun produit</h3>
            <p className="mt-1 text-sm text-gray-500">
              Aucun produit ne correspond aux critères de recherche
            </p>
          </div>
        ) : viewMode === 'list' ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50/50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Produit
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Stock
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Emplacement
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Marque
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white/50 divide-y divide-gray-200">
                {products.map((product) => (
                  <tr key={product.id} className="hover:bg-gray-50/50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {product.image && (
                          <img
                            className="h-10 w-10 rounded-lg object-cover mr-4"
                            src={product.image}
                            alt={product.designation}
                          />
                        )}
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {product.designation}
                          </div>
                          <div className="text-sm text-gray-500">
                            Réf: {product.reference}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <span className="text-sm font-medium text-gray-900">
                          {product.currentStock}
                        </span>
                        {productService.isStockAlert(product) && (
                          <AlertTriangle className="h-4 w-4 text-orange-500 ml-2" />
                        )}
                      </div>
                      <div className="text-xs text-gray-500">
                        Seuil: {product.minThreshold}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {productService.getLocationName(product.defaultLocationId)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {productService.getBrandName(product.brandId)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        <Link
                          to={`/products/${product.id}`}
                          className="text-blue-600 hover:text-blue-900 p-1 rounded"
                          title="Voir les détails"
                        >
                          <Eye className="h-4 w-4" />
                        </Link>
                        <button
                          onClick={() => handleAddToCart(product.id)}
                          className="text-green-600 hover:text-green-900 p-1 rounded"
                          title="Ajouter au panier"
                        >
                          <ShoppingCart className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 p-6">
            {products.map((product) => (
              <div key={product.id} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow">
                {product.image && (
                  <img
                    className="w-full h-48 object-cover"
                    src={product.image}
                    alt={product.designation}
                  />
                )}
                <div className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="text-sm font-medium text-gray-900 line-clamp-2">
                      {product.designation}
                    </h3>
                    {productService.isStockAlert(product) && (
                      <AlertTriangle className="h-4 w-4 text-orange-500 flex-shrink-0 ml-2" />
                    )}
                  </div>
                  
                  <p className="text-xs text-gray-500 mb-2">
                    Réf: {product.reference}
                  </p>
                  
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-medium text-gray-900">
                      Stock: {product.currentStock}
                    </span>
                    <span className="text-xs text-gray-500">
                      {productService.getBrandName(product.brandId)}
                    </span>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <Link
                      to={`/products/${product.id}`}
                      className="text-blue-600 hover:text-blue-900 text-sm font-medium"
                    >
                      Détails
                    </Link>
                    <button
                      onClick={() => handleAddToCart(product.id)}
                      className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded text-sm transition-colors"
                    >
                      Panier
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default ProductList;